import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, LogOut, FileCheck2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Brand } from "@/components/Sidebar";
import { DocViewer, DocCheckRow, ConsentRow, SectionTitle, acceptText } from "@/components/legal/LegalParts";

export function LegalGate() {
  const { user, refresh, logout } = useAuth();
  const [data, setData] = useState(null);
  const [checked, setChecked] = useState({});
  const [consents, setConsents] = useState({});
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/legal/pending").then(({ data: d }) => { setData(d); setChecked({}); }).catch((x) => toast.error(formatApiError(x.response?.data?.detail)));
  useEffect(() => { load(); }, []);
  const docs = data?.documents || [];
  const allOk = docs.every((d) => checked[d.version_id]);

  const submit = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/legal/accept", { version_ids: docs.filter((d) => checked[d.version_id]).map((d) => d.version_id), consents });
      if (r.pending.length) { toast.error("Onaylanması gereken belgeler var"); await load(); }
      else { toast.success("Onaylarınız kaydedildi"); await refresh(); }
    } catch (x) { toast.error(formatApiError(x.response?.data?.detail)); await load(); }
    setBusy(false);
  };

  return (
    <div className="min-h-screen bg-[#f4f8f6] flex items-start sm:items-center justify-center p-4 sm:p-8" data-testid="legal-gate">
      <div className="w-full max-w-2xl bg-white rounded-3xl shadow-[0_30px_80px_-40px_rgba(4,120,87,0.45)] border border-slate-200/70 p-6 sm:p-8 space-y-6">
        <div className="flex items-center justify-between"><Brand /><Button variant="ghost" size="sm" onClick={logout} data-testid="legal-gate-logout"><LogOut className="w-4 h-4 mr-1.5" />Çıkış</Button></div>
        <div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>Sözleşme onayları</h1>
          <p className="text-slate-500 mt-1 text-sm">Merhaba {user.full_name}. Panele geçmeden önce aşağıdaki belgeleri okuyup onaylamanız gerekiyor. Onayladığınız belgeler tekrar sorulmaz; yeni bir sürüm yayımlanırsa yeniden onayınız istenebilir.</p>
        </div>
        {!data ? <Loader2 className="w-5 h-5 animate-spin text-slate-400" /> : (
          <>
            {docs.length > 0 && (
              <div className="space-y-2">
                <SectionTitle icon={FileCheck2} title="Zorunlu sözleşme ve bildirimler" text="Hizmetin sunulabilmesi için gereklidir." />
                {docs.map((d) => <DocCheckRow key={d.id} doc={d} checked={!!checked[d.version_id]} onChange={(v) => setChecked({ ...checked, [d.version_id]: v })} onOpen={() => setOpen(d)} />)}
              </div>
            )}
            {data.consents.length > 0 && (
              <div className="space-y-2">
                <SectionTitle title="İsteğe bağlı izinler" text="Vermeniz zorunlu değildir; panel kullanımını etkilemez. Dilediğiniz zaman Sözleşmelerim ve Onaylarım ekranından değiştirebilirsiniz." />
                {data.consents.map((c) => <ConsentRow key={c.id} doc={c} value={consents[c.version_id]} onChange={(v) => setConsents({ ...consents, [c.version_id]: v })} onOpen={() => setOpen(c)} />)}
              </div>
            )}
            <Button className="w-full h-11 bg-emerald-700 hover:bg-emerald-800" disabled={!allOk || busy} onClick={submit} data-testid="legal-gate-submit">
              {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Onayla ve panele geç
            </Button>
            {!allOk && <div className="text-xs text-slate-500 text-center" data-testid="legal-gate-hint">Devam etmek için zorunlu belgelerin tamamını onaylayın.</div>}
          </>
        )}
      </div>
      <DocViewer doc={open} onClose={() => setOpen(null)} acceptLabel={open && acceptText(open)}
        onAccept={open && open.kind !== "CONSENT" ? () => setChecked((c) => ({ ...c, [open.version_id]: true })) : null} />
    </div>
  );
}
